import type { AnalysisAPIError, AnalysisJobTerminalFailure } from "./analysis-jobs";
import type { createTranslator, TranslationKey } from "./i18n";
import { parseMarketDataHistoryLimit, type MarketDataHistoryLimit } from "./market-data-error.ts";

type Translator = ReturnType<typeof createTranslator>;
type LocalizableAPIError = Pick<
  AnalysisAPIError | AnalysisJobTerminalFailure,
  "reason" | "code" | "localizationKey" | "localizedMessage"
> & { marketDataHistoryLimit?: MarketDataHistoryLimit };

const reasonTranslationKeys: Record<string, TranslationKey> = {
  account_deleted: "signInUnavailable",
  account_data_deletion_pending: "signInUnavailable",
  firebase_identity_deletion_pending: "signInUnavailable",
  account_write_conflict: "accountSignInUnavailableHint",
  normal_backtest_quota_exceeded: "currentPlanCta",
  backtest_request_limit_exceeded: "backtestFailed",
  history_request_limit_exceeded: "accountSyncHint",
  invalid_backtest_request: "backtestFailed",
  invalid_period: "periodAdjusted",
  period_limit_exceeded: "periodClampedByPlan",
  period_clamped_to_plan_limit: "periodClampedByPlan",
  market_data_unavailable: "backtestFailed",
  custom_strategy_not_executable: "backtestFailed",
  invalid_strategy_dsl: "backtestFailed",
  strategy_builder_dsl_required: "backtestFailed",
  strategy_builder_unavailable: "backtestFailed",
  strategy_builder_request_limit_exceeded: "backtestFailed",
  ai_credit_quota_exceeded: "aiAnalysisFailed",
  analysis_request_limit_exceeded: "aiAnalysisFailed",
  invalid_analysis_request: "aiAnalysisFailed",
  invalid_analysis_job_request: "aiAnalysisFailed",
  analysis_job_active_limit: "loadingAnalysisTitle",
  analysis_job_create_rate_limited: "aiAnalysisFailed",
  analysis_job_expired: "aiAnalysisFailed",
  analysis_job_failed: "aiAnalysisFailed",
  analysis_job_not_found: "aiAnalysisFailed",
  analysis_job_result_missing: "aiAnalysisFailed",
  analysis_jobs_disabled: "aiAnalysisFailed",
  analysis_job_queue_failed: "aiAnalysisFailed",
  analysis_job_dispatch_failed: "aiAnalysisFailed",
  analysis_job_persistence_failed: "aiAnalysisFailed",
  analysis_job_recovery_pending: "loadingAnalysisTitle",
  analysis_job_quota_release_pending: "loadingAnalysisTitle",
  ai_provider_quota_exhausted: "aiAnalysisFailed",
  ai_provider_rate_limited: "aiAnalysisFailed",
  ai_provider_configuration_error: "aiAnalysisFailed",
  ai_provider_invalid_response: "aiAnalysisFailed",
  ai_provider_unavailable: "aiAnalysisFailed",
  analysis_worker_auth_unavailable: "aiAnalysisFailed",
  locked_holdout_trial_not_found: "lockedHoldoutFailed",
  locked_holdout_trial_expired: "lockedHoldoutFailed",
  locked_holdout_evaluation_failed: "lockedHoldoutFailed",
  locked_holdout_result_unavailable: "lockedHoldoutFailed",
  locked_holdout_lease_unavailable: "lockedHoldoutFailed",
  locked_holdout_result_persistence_pending: "runningLockedHoldout",
  bot_tick_auth_unavailable: "tickBotFailed",
};

function normalizedContractKey(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (reasonTranslationKeys[normalized]) return normalized;
  return Object.keys(reasonTranslationKeys).find((candidate) => normalized.endsWith(candidate));
}

export function localizedAPIErrorMessage(
  error: unknown,
  t: Translator,
  fallbackKey: TranslationKey,
): string {
  if (error && typeof error === "object") {
    const apiError = error as Partial<LocalizableAPIError>;
    const historyLimit = parseMarketDataHistoryLimit(apiError.marketDataHistoryLimit);
    if (apiError.reason === "market_data_unavailable" && historyLimit) {
      const limits = historyLimit.limits;
      return t("marketDataHistoryLimit")
        .replace("{interval}", limits.interval)
        .replace("{days}", String(limits.history_days))
        .replace("{start}", limits.earliest_start_date.replace("T", " ").replace("Z", " UTC"))
        .replace("{end}", limits.latest_end_date.replace("T", " ").replace("Z", " UTC"))
        .replace("{intervals}", limits.suggested_intervals.join(" / "));
    }
    const stableKey = [apiError.reason, apiError.code, apiError.localizationKey]
      .map(normalizedContractKey)
      .find((value): value is string => Boolean(value));
    if (stableKey) return t(reasonTranslationKeys[stableKey]);

    const localizedMessage = apiError.localizedMessage?.trim();
    if (localizedMessage) return localizedMessage;
  }
  return t(fallbackKey);
}
